import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import { sendRequest } from '../../utils/dateHelpers';
import Loading from '../../components/Loading';
import { getValidDevFeedCursor } from '../../utils/devFeedCursor';
import {
  getAverageDeveloperCostFromSource,
  parseNumericInput,
  shouldFetchAverageDeveloperCostFromEndpoint
} from '../../utils/companyConfigurations';

const FEED_RENDER_PAGE_SIZE = 30;
const FEED_REQUEST_PAGE_SIZE = 100;

const getAverageDeveloperCost = userInfo => {
  const directCost = getAverageDeveloperCostFromSource(userInfo);
  if (directCost > 0) return directCost;
  return getAverageDeveloperCostFromSource(userInfo?.user);
};

const convertEffortToInvestment = (value, averageCost) => {
  const effortValue = parseNumericInput(value);

  return Math.round(
    ((Number.isFinite(effortValue) ? effortValue : 0) / 60) * averageCost
  );
};

const normalizeDisplayText = (value, fallback = '-') => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue || fallback;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
};

const normalizeTagLabel = value => normalizeDisplayText(value, '').trim();

export const getProjectTagLabels = hashtags =>
  (Array.isArray(hashtags?.projects)
    ? hashtags.projects
    : [hashtags?.project_name || hashtags?.project]
  )
    .map(normalizeTagLabel)
    .filter(Boolean);

const DevFeedSkeleton = () => (
  <div role="status" aria-label="Loading developer feed" className="space-y-3">
    {[0, 1, 2].map(index => (
      <article
        key={index}
        className="animate-pulse rounded-md border border-stroke bg-white p-4 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-5"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="h-4 w-36 rounded bg-slate-200 dark:bg-slate-700" />
          <div className="h-4 w-20 rounded bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="mt-4 h-6 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
        <div className="mt-2 h-4 w-full rounded bg-slate-200 dark:bg-slate-700" />
        <div className="mt-2 h-4 w-5/6 rounded bg-slate-200 dark:bg-slate-700" />
        <div className="mt-4 flex gap-2">
          <div className="h-6 w-20 rounded-full bg-slate-200 dark:bg-slate-700" />
          <div className="h-6 w-24 rounded-full bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="mt-4 border-t border-dashed border-stroke pt-3 dark:border-strokedark">
          <div className="h-4 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
        </div>
      </article>
    ))}
    <span className="sr-only">Loading feed...</span>
  </div>
);

const DevFeed = () => {
  const { userInfo = {} } = useSelector(state => state);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fallbackAverageDeveloperCost, setFallbackAverageDeveloperCost] =
    useState(0);

  const sentinelRef = useRef(null);
  const latestRequestRef = useRef(0);
  const skipNextDebouncedFetchRef = useRef(false);
  const cursorRef = useRef(null);
  const isLoadingRef = useRef(false);
  const bufferedItemsRef = useRef([]);
  const backendHasMoreRef = useRef(true);
  const roleBasedAverageDeveloperCost = getAverageDeveloperCost(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : fallbackAverageDeveloperCost;

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    const fetchAverageDeveloperCost = async () => {
      try {
        const response = await sendRequest('/company-configurations', 'GET');
        const fallbackCost = getAverageDeveloperCostFromSource(response);

        if (fallbackCost > 0) {
          setFallbackAverageDeveloperCost(fallbackCost);
        }
      } catch (fetchError) {
        console.error(
          'Failed to load fallback average developer cost',
          fetchError
        );
      }
    };

    if (
      roleBasedAverageDeveloperCost > 0 ||
      !shouldFetchAverageDeveloperCostFromEndpoint(
        userInfo?.role,
        userInfo?.user?.role
      )
    ) {
      return;
    }

    fetchAverageDeveloperCost();
  }, [roleBasedAverageDeveloperCost, userInfo?.role, userInfo?.user?.role]);

  const appendBufferedItems = useCallback(() => {
    if (!bufferedItemsRef.current.length) return false;

    const nextChunk = bufferedItemsRef.current.slice(0, FEED_RENDER_PAGE_SIZE);
    bufferedItemsRef.current = bufferedItemsRef.current.slice(
      FEED_RENDER_PAGE_SIZE
    );

    setItems(prev => [...prev, ...nextChunk]);
    setHasMore(
      bufferedItemsRef.current.length > 0 || backendHasMoreRef.current
    );

    return true;
  }, []);

  const loadFeed = useCallback(
    async ({ append, searchTerm = debouncedSearch }) => {
      if (append && isLoadingRef.current) return;
      if (append && appendBufferedItems()) return;

      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      setIsLoading(true);
      setError('');

      try {
        const body = {
          limit: FEED_REQUEST_PAGE_SIZE,
          filters: {
            search: searchTerm.trim(),
            projects: [],
            repos: [],
            developers: [],
            categories: []
          }
        };

        const validCursor = getValidDevFeedCursor(cursorRef.current);
        if (append && validCursor) {
          body.cursor = validCursor;
        }

        const response = await sendRequest('/dev-feed', 'POST', body);

        if (requestId !== latestRequestRef.current) return;

        const fetchedItems = response?.data || [];
        const pagination = response?.pagination || {};
        const visibleItems = fetchedItems.slice(0, FEED_RENDER_PAGE_SIZE);

        bufferedItemsRef.current = fetchedItems.slice(FEED_RENDER_PAGE_SIZE);
        backendHasMoreRef.current = Boolean(pagination?.hasMore);

        setItems(prev => (append ? [...prev, ...visibleItems] : visibleItems));
        setCursor(pagination?.nextCursor || null);
        setHasMore(
          bufferedItemsRef.current.length > 0 || backendHasMoreRef.current
        );
      } catch (requestError) {
        const message = normalizeDisplayText(
          requestError?.message,
          'Failed to load dev feed.'
        );
        setError(message);
        if (append && message.includes('Invalid cursor id')) {
          setHasMore(false);
        }
      } finally {
        if (requestId === latestRequestRef.current) {
          setIsLoading(false);
        }
      }
    },
    [appendBufferedItems, debouncedSearch]
  );

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchInput]);

  useEffect(() => {
    if (skipNextDebouncedFetchRef.current) {
      skipNextDebouncedFetchRef.current = false;
      return;
    }

    setItems([]);
    bufferedItemsRef.current = [];
    backendHasMoreRef.current = true;
    setCursor(null);
    setHasMore(true);
    loadFeed({ append: false, searchTerm: debouncedSearch });
  }, [debouncedSearch, loadFeed]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading) {
          loadFeed({ append: true, searchTerm: debouncedSearch });
        }
      },
      { rootMargin: '250px' }
    );

    const currentRef = sentinelRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [debouncedSearch, hasMore, isLoading, loadFeed]);

  const onSearchChange = value => {
    setItems([]);
    bufferedItemsRef.current = [];
    backendHasMoreRef.current = true;
    setCursor(null);
    setHasMore(true);
    setSearchInput(value);
  };

  const onTagClick = value => {
    if (!value) return;
    const normalizedValue = value.trim();

    skipNextDebouncedFetchRef.current = true;

    setItems([]);
    bufferedItemsRef.current = [];
    backendHasMoreRef.current = true;
    setCursor(null);
    setHasMore(true);
    setSearchInput(normalizedValue);
    setDebouncedSearch(normalizedValue);
    loadFeed({ append: false, searchTerm: normalizedValue });
  };

  return (
    <div className="space-y-5">
      <section className="rounded-md border border-stroke bg-white p-4 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-6">
        <div className="flex flex-col gap-2">
          <label
            htmlFor="devfeed-search"
            className="text-sm font-semibold text-slate-700 dark:text-slate-200"
          >
            Search
          </label>
          <input
            id="devfeed-search"
            type="text"
            value={searchInput}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search impact, project, repo, developer, or category..."
            className="w-full rounded-lg border border-stroke bg-white px-4 py-2 text-sm text-slate-800 outline-none transition focus:border-primary dark:border-strokedark dark:bg-meta-4 dark:text-white"
          />
        </div>
      </section>

      <section className="space-y-3">
        {isLoading && !items.length && <DevFeedSkeleton />}

        {items.map(item => {
          const stats = item?.stats || {};
          const hashtags = item?.hashtags || {};
          const commitSha = item?.commit_sha || '';
          const shortCommitSha = commitSha ? commitSha.slice(0, 6) : '';
          const commitUrl = item?.commit_url || '';
          const value =
            averageDeveloperCost > 0
              ? convertEffortToInvestment(stats?.rev, averageDeveloperCost)
              : typeof stats?.value === 'number'
                ? Math.round(stats.value)
                : 0;
          const authorName = normalizeDisplayText(
            item?.author?.name,
            'Unknown Developer'
          );
          const impactTitle = normalizeDisplayText(
            item?.impact_summary?.title,
            'Untitled impact'
          );
          const impactDetail = normalizeDisplayText(
            item?.impact_summary?.detail
          );
          const projectLabels = getProjectTagLabels(hashtags);
          const repoLabel = normalizeTagLabel(
            hashtags.repo_name || hashtags.repo
          );
          const categoryLabels = (
            Array.isArray(hashtags?.categories) ? hashtags.categories : []
          )
            .map(normalizeTagLabel)
            .filter(Boolean);

          return (
            <article
              key={item?.id || `${item?.timestamp}-${authorName}`}
              className="rounded-md border border-stroke bg-white p-4 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md dark:border-strokedark dark:bg-boxdark sm:p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500 dark:text-slate-300">
                <div className="font-semibold text-slate-700 dark:text-slate-100">
                  👤 {authorName}
                </div>
                <div>
                  ⏱{' '}
                  {item?.timestamp
                    ? formatDistanceToNow(new Date(item.timestamp), {
                        addSuffix: true
                      })
                    : '-'}
                </div>
              </div>

              <div className="mt-3 flex items-start justify-between gap-3">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {impactTitle}
                </h3>
                {shortCommitSha && commitUrl && (
                  <a
                    href={commitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                  >
                    {shortCommitSha}
                  </a>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {impactDetail}
              </p>

              <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                {projectLabels.map((project, index) => (
                  <button
                    key={`${project}-${index}`}
                    type="button"
                    onClick={() => onTagClick(project)}
                    className="rounded-full bg-blue-50 px-3 py-1 text-blue-700 hover:bg-blue-100"
                  >
                    #{project}
                  </button>
                ))}
                {repoLabel && (
                  <button
                    type="button"
                    onClick={() => onTagClick(repoLabel)}
                    className="rounded-full bg-purple-50 px-3 py-1 text-purple-700 hover:bg-purple-100"
                  >
                    #{repoLabel}
                  </button>
                )}
                {categoryLabels.map((category, index) => (
                  <button
                    key={`${category}-${index}`}
                    type="button"
                    onClick={() => onTagClick(category)}
                    className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 hover:bg-emerald-100"
                  >
                    #{category}
                  </button>
                ))}
              </div>

              <div className="mt-4 border-t border-dashed border-stroke pt-3 text-sm text-slate-600 dark:border-strokedark dark:text-slate-300">
                <span className="font-semibold">REV:</span> {stats?.rev || 0}
                <span className="mx-2">|</span>
                <span className="font-semibold">
                  +{stats?.insertions || 0}
                </span>{' '}
                / -{stats?.deletions || 0} lines
                <span className="mx-2">|</span>
                <span className="font-semibold">Value:</span> ${value}
              </div>
            </article>
          );
        })}

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {isLoading && items.length > 0 && <Loading type="bar" />}

        {!isLoading && !items.length && !error && (
          <div className="rounded border border-stroke bg-white px-4 py-3 text-sm text-slate-500 dark:border-strokedark dark:bg-boxdark dark:text-slate-300">
            No feed entries found for current filters.
          </div>
        )}

        {!hasMore && items.length > 0 && (
          <div className="px-2 py-3 text-center text-xs text-slate-400">
            You&apos;ve reached the end of the feed.
          </div>
        )}

        <div ref={sentinelRef} className="h-1" />
      </section>
    </div>
  );
};

export default DevFeed;
